import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Stack,
  Paper,
  Grid,
} from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend as RechartsLegend,
} from "recharts";
import { format, subDays, differenceInDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface AlertData {
  date: string;
  totalAlerts: number;
  numStudies: number;
}

interface AlertsSummary {
  totalAlerts: number;
  totalDays: number;
  daysWithAlerts: number;
  averageAlertsPerDay: number;
}

const AlertsAnalytics: React.FC = () => {
  const [data, setData] = useState<AlertData[]>([]);
  const [summary, setSummary] = useState<AlertsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(() =>
    format(subDays(new Date(), 15), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      fetchAlertsData(startDate, endDate);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchAlertsData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.get(
        `${API_ENDPOINTS.ADHERENCE_MONITOR.ALERTS_ANALYTICS}?startDate=${start}&endDate=${end}`
      );

      if (response.success) {
        setData(response.data);
        setSummary(response.summary);
      } else {
        setData([]);
        setSummary(null);
      }
    } catch (error) {
      console.error("Error fetching alerts data:", error);
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchAlertsData(startDate, endDate);
  };

  const handleResetDateRange = (days: number) => {
    const newStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const newEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchAlertsData(newStart, newEnd);
  };

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{ fontWeight: 600, color: "#8C1515" }}
          >
            Alerts Analytics
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {/* <InfoIcon sx={{ fontSize: 18, color: "#FF6B6B" }} /> */}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Total alerts generated and number of studies involved per day.
            </Typography>
          </Box>
        </Box>

        {/* Date Range Controls */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              onClick={handleApplyDateRange}
              sx={{
                mt: 1,
                backgroundColor: "#8C1515",
                "&:hover": { backgroundColor: "#6B0E0E" },
              }}
            >
              Apply
            </Button>
          </Stack>
          {dateRangeError && (
            <Typography sx={{ color: "#F44336", fontSize: 12, mt: 1 }}>
              {dateRangeError}
            </Typography>
          )}
        </Box>

        {/* Preset Buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(7)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 7 Days
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(15)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 15 Days
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(30)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 30 Days
          </Button>
        </Stack>

        {/* Summary Stats */}
        {summary && !loading && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#FEF7F7" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Total Alerts
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#8C1515", mt: 0.5 }}
                >
                  {summary.totalAlerts}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Days with Alerts
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {summary.daysWithAlerts} / {summary.totalDays}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Avg Alerts/Day
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {summary.averageAlertsPerDay}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Date Range
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {startDate} to {endDate}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Chart */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data.length > 0 ? (
          <Box sx={{ width: "100%", height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: "Total Alerts",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: "Number of Studies",
                    angle: 90,
                    position: "insideRight",
                  }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    padding: 10,
                  }}
                  formatter={(value, name) => {
                    if (name === "totalAlerts") return [value, "Total Alerts"];
                    if (name === "numStudies") return [value, "Studies"];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <RechartsLegend wrapperStyle={{ paddingTop: 20 }} />
                <Bar
                  yAxisId="left"
                  dataKey="totalAlerts"
                  fill="#8C1515"
                  name="Total Alerts"
                  opacity={0.8}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="numStudies"
                  stroke="#00505C"
                  strokeWidth={2}
                  name="Studies"
                  dot={{ fill: "#00505C", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          !loading && (
            <Alert severity="info">
              No alerts found for the selected date range.
            </Alert>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsAnalytics;
